import React, { lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import ProtectedRoute from "./utils/protectedRoutes/protectedRoutes";
import AdminLayout from "./layouts/adminLayout";
import DefaultLayout from "./layouts/defaultLayout";
import AuthLayout from "./layouts/authLayout";
import ApplyExpert from "./pages/applyExpert/applyExpert";
import Arbitrator from "./pages/arbitrators/arbitrator";

const Faqs = lazy(() => import("./pages/faqs/faqs"));
const JuryPanel = lazy(() => import("./pages/juryPanel"));
const Technical = lazy(() => import("./pages/technical"));
const RoleOfTheCenter = lazy(() => import("./pages/roleOfCenter"));
const NewsDetail = lazy(() => import("./pages/news/newsDetail/newsDetail"));
const TraningCoursesList = lazy(
  () => import("./pages/mediaCenter/trainingCourses/traningCoursesList"),
);
const SeminarsList = lazy(
  () => import("./pages/mediaCenter/seminars/seminarsListing"),
);
const SeminarsDetail = lazy(
  () => import("./pages/mediaCenter/seminars/seminarsDetail"),
);
const EventsList = lazy(() => import("./pages/events"));
const EventDetail = lazy(
  () => import("./pages/events/eventDetail/eventDetail"),
);
const WorkshopsList = lazy(
  () => import("./pages/mediaCenter/workshop/workshopsList"),
);
const WorkshopDetail = lazy(
  () => import("./pages/mediaCenter/workshop/workshopsDetail"),
);
const LibraryList = lazy(
  () =>
    import("./pages/mediaCenter/electronicLibrary/electronicLibraryListing"),
);
const LibraryDetail = lazy(
  () => import("./pages/mediaCenter/electronicLibrary/electronicLibraryDetail"),
);
const PhotosList = lazy(
  () => import("./pages/mediaCenter/photoAlbum/photoAlbumList"),
);
const VideosList = lazy(
  () => import("./pages/mediaCenter/videos/videosListPage"),
);
const VideoDetail = lazy(
  () => import("./pages/mediaCenter/videos/videosDetail"),
);
const PrivacyPolicy = lazy(() => import("./pages/privacyPolicy/privacyPolicy"));
const TermsAndCondition = lazy(
  () => import("./pages/mediaCenter/termsAndCondtion/termsAndCondtion"),
);
const RefundCancellationPolicy = lazy(
  () =>
    import(
      "./pages/mediaCenter/legalInformation/refundCancellationPolicy/refundCancellationPolicy"
    ),
);
const ComplianceApplicableLaws = lazy(
  () =>
    import(
      "./pages/mediaCenter/legalInformation/complianceApplicableLaws/complianceApplicableLaws"
    ),
);
const ArbitrationClause = lazy(
  () =>
    import(
      "./pages/mediaCenter/legalInformation/arbitrationClause/arbitrationClause"
    ),
);
const IntellectualPropertyNotice = lazy(
  () =>
    import(
      "./pages/mediaCenter/legalInformation/intellectualPropertyNotice/intellectualPropertyNotice"
    ),
);
const ServiceDetail = lazy(
  () => import("./pages/services/serviceDetailPage/serviceDetail"),
);
const LawDetail = lazy(() => import("./pages/laws/lawDetails"));
const ArbitrationForm = lazy(
  () => import("./pages/services/servicesForms/arbitrationForm"),
);
const MediationForm = lazy(
  () => import("./pages/services/servicesForms/mediationForm"),
);
const CalculatorFees = lazy(
  () => import("./pages/calculatorFees/calculatorFees"),
);
const ChairmanMessage = lazy(
  () => import("./pages/chairmanMessage​/chairmanMessage​"),
);
const GeneralSecretaryMessage = lazy(
  () => import("./pages/generalSecretaryMessage/generalSecretaryMessage"),
);
const Partners = lazy(() => import("./pages/partners/partners"));
const CommitteeMembers = lazy(
  () => import("./pages/committeeMembers/committeeMembers"),
);
const WorkTeams = lazy(() => import("./pages/workTeams/workTeams"));
const ArbitrationUAE = lazy(() => import("./pages/arbitrationUAE"));
const WorkTeamDetail = lazy(() => import("./pages/workTeams/workTeamDetails"));
const CommiteeMemberDetail = lazy(
  () => import("./pages/committeeMembers/committeeMembersDetail"),
);
const CodeOfConduct = lazy(() => import("./pages/codeOfConduct/codeOfConduct"));
const ArbitrationMediationLogistics = lazy(
  () => import("./pages/arbitrationMediationLogistics"),
);
const HallDetail = lazy(
  () => import("./pages/arbitrationMediationLogistics/hallDetail"),
);
const PhotoAlbumsList = lazy(() => import("./pages/mediaCenter/photoAlbum"));
const MemberOfArbitrators = lazy(
  () => import("./pages/memberOfArbitrators/memberOfArbitrators"),
);
const Conference = lazy(() => import("./pages/mediaCenter/courses/conference"));
const ArbitrationTrainingProgram = lazy(
  () => import("./pages/mediaCenter/courses/arbitrationTrainingProgram"),
);
const Seminar = lazy(() => import("./pages/mediaCenter/courses/seminar"));
const DiplomaCourseInternationalArbitration = lazy(
  () =>
    import("./pages/mediaCenter/courses/diplomaCourseInternationalArbitration"),
);
const LawTypes = lazy(() => import("./pages/aboutLaws/lawTypes/lawTypes"));
const CourseDetail = lazy(
  () => import("./pages/mediaCenter/courses/courseDetail/courseDetails"),
);
const CoursePaymentSuccess = lazy(
  () => import("./pages/mediaCenter/courses/courseDetail/coursePaymentSuccess"),
);
const WorkshopPaymentSuccess = lazy(
  () => import("./pages/mediaCenter/workshop/workshopPaymentSuccess"),
);
const EventPaymentSuccess = lazy(
  () => import("./pages/events/eventDetail/eventPaymentSuccess"),
);
const ReadPDF = lazy(() => import("./pages/readPDF/readPDF"));
const CountryAlbum = lazy(
  () => import("./pages/aboutLaws/countryAlbum/countryAlbum"),
);
const AlbumList = lazy(
  () => import("./pages/aboutLaws/albumList/countryAlbum"),
);
const ApplyArbitrator = lazy(
  () => import("./pages/applyArbitrator/applyArbitrator"),
);
const RegisterForm = lazy(() => import("./components/register/register-new"));
const AccountVerified = lazy(
  () => import("./pages/accountVerified/accountVerified"),
);
const UaeAccountVerified = lazy(
  () => import("./pages/accountVerified/uaeAccountVerified"),
);
const NotFound = lazy(() => import("./components/404Page/404Page"));
const Login = lazy(() => import("./components/login/login"));
const Profile = lazy(
  () => import("./components/userDashboard/profile/profile"),
);
const Dashboard = lazy(() => import("./components/userDashboard/dashboard"));
const Home = lazy(() => import("./pages/home"));
const LoginForm = lazy(() => import("./pages/auth/LoginForm"));
const AboutUs = lazy(() => import("./pages/aboutUs"));
const News = lazy(() => import("./pages/news"));
const MediaCenter = lazy(() => import("./pages/mediaCenter"));
const AboutLaws = lazy(() => import("./pages/aboutLaws/index"));
const Services = lazy(() => import("./pages/services"));
const ArbitrationPage = lazy(() => import("./pages/arbitration"));
const DepartmentsOfCenter = lazy(() => import("./pages/departmentsOfCenter"));
const Mediation = lazy(() => import("./pages/mediation"));
const MediationRequestForm = lazy(
  () => import("./pages/mediationRequestForm/mediationRequestForm"),
);
const ResetPasswordComponent = lazy(
  () => import("./components/resetPassword/resetPassword"),
);
const E_Services = lazy(
  () => import("./components/userDashboard/e-services/e-services"),
);
const Arbitration = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/arbitration/arbitration"
    ),
);
const CertifiedCopy = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/certifiedCopy/certifiedCopy"
    ),
);
const Interpretation = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/interpretation/interpretation"
    ),
);
const Memberships = lazy(
  () => import("./components/userDashboard/e-services/memberships/memberships"),
);
const ExpertRegistration = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/memberships/expertComponent/expertRegistration/expertRegistration"
    ),
);
const ArbitratorRegistration = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/memberships/arbitratorComponent/arbitratorRegistration/arbitratorRegistraion"
    ),
);
const ArbitratorDashboard = lazy(
  () => import("./components/arbitrator/dashboard/dashboard"),
);
const ArbitratorProfile = lazy(
  () => import("./components/arbitrator/dashboard/profile.js/profile"),
);
const Intervention = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/intervention/intervention"
    ),
);
const Joinder = lazy(
  () => import("./components/userDashboard/e-services/request/joinder/joinder"),
);
const Claim = lazy(
  () => import("./components/userDashboard/e-services/request/claim/claim"),
);
const UserApplication = lazy(
  () =>
    import(
      "./components/arbitrator/dashboard/applications/userApplications/userApplication"
    ),
);
const ApplicationStatus = lazy(
  () =>
    import(
      "./components/arbitrator/dashboard/applications/userApplications/applicationStatus"
    ),
);
const CenterTable = lazy(() => import("./pages/centerTables"));
const Objection = lazy(
  () =>
    import("./components/userDashboard/e-services/request/objection/objection"),
);
const Revocation = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/revocation/revocation"
    ),
);
const Certificate = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/request/certificate/certificate"
    ),
);
const Membership = lazy(() => import("./pages/membership"));
const NonListedArbitrator = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/nonListedArbitrator/nonListedArbitrator"
    ),
);
const ArbitratorNotGoverned = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/arbitratorNotGoverned/arbitratorNotGoverned"
    ),
);
const ContactUs = lazy(() => import("./pages/contactUs"));
const MembershipRegistration = lazy(
  () =>
    import(
      "./components/userDashboard/e-services/memberships/membershipRegistration/membershipRegistration"
    ),
);

function Routes() {
  const router = createBrowserRouter([
    {
      element: <AuthLayout />,
      errorElement: <Navigate to="/login" />,
      children: [
        {
          path: "/login",
          element: <Login />,
        },
        {
          path: "/login2",
          element: <LoginForm />,
        },
        // {
        //   path: "/register",
        //   element: <Register />,
        // },
        {
          path: "/resetPassword",
          element: <ResetPasswordComponent />,
        },
      ],
    },
    {
      element: <DefaultLayout />,
      errorElement: <Navigate to="/login" />,
      children: [
        {
          path: "/register",
          element: <RegisterForm />,
        },
        {
          path: "/home",
          element: <Home />,
        },
        {
          path: "/",
          element: <Home />,
        },
        {
          path: "/about-us",
          element: <AboutUs />,
        },
        {
          path: "/events",
          element: <EventsList />,
        },
        {
          path: "/event/:id/payment-success",
          element: <EventPaymentSuccess />,
        },
        {
          path: "/event/:id",
          element: <EventDetail />,
        },
        {
          path: "/news",
          element: <News />,
        },
        {
          path: "/detail/:id",
          element: <NewsDetail />,
        },
        {
          path: "/legal-article",
          element: <MediaCenter />,
        },
        {
          path: "/courses",
          element: <TraningCoursesList />,
        },
        {
          path: "/courses/:id/payment-success",
          element: <CoursePaymentSuccess />,
        },
        {
          path: "/courses/:id",
          element: <CourseDetail />,
        },
        {
          path: "/seminars",
          element: <SeminarsList />,
        },
        {
          path: "/seminars/:id",
          element: <SeminarsDetail />,
        },
        {
          path: "/workshops",
          element: <WorkshopsList />,
        },
        {
          path: "/workshops/:id/payment-success",
          element: <WorkshopPaymentSuccess />,
        },
        {
          path: "/workshops/:id",
          element: <WorkshopDetail />,
        },
        {
          path: "/libraries",
          element: <LibraryList />,
        },
        {
          path: "/libraries/:id",
          element: <LibraryDetail />,
        },
        {
          path: "/photo-albums",
          element: <PhotoAlbumsList />,
        },
        {
          path: "/photo-albums/:id",
          element: <PhotosList />,
        },
        {
          path: "/videos",
          element: <VideosList />,
        },
        {
          path: "/videos/:id",
          element: <VideoDetail />,
        },
        {
          path: "/law-library",
          element: <AboutLaws />,
        },
        {
          path: "/law/:id",
          element: <LawTypes />,
        },
        {
          path: "/law/read/:id",
          element: <ReadPDF />,
        },
        {
          path: "/law/album/:id",
          element: <CountryAlbum />,
        },
        {
          path: "/laws/:id",
          element: <LawDetail />,
        },
        {
          path: "/law/country-album/:id",
          element: <AlbumList />,
        },
        {
          path: "/jury-panel",
          element: <JuryPanel />,
        },
        {
          path: "/services",
          element: <Services />,
        },
        {
          path: "/services/:id",
          element: <ServiceDetail />,
        },
        {
          path: "/arbitration-form",
          element: <ArbitrationForm />,
        },
        {
          path: "/mediation-form",
          element: <MediationForm />,
        },
        {
          path: "/arbitration",
          element: <ArbitrationPage />,
        },
        {
          path: "/technical",
          element: <Technical />,
        },
        {
          path: "/mediation",
          element: <Mediation />,
        },
        {
          path: "/fee-calculator",
          element: <CalculatorFees />,
        },
        {
          path: "/chairman-message",
          element: <ChairmanMessage />,
        },
        {
          path: "/general-secretary-message",
          element: <GeneralSecretaryMessage />,
        },
        {
          path: "/partners",
          element: <Partners />,
        },
        // {
        //   path: "/partners/:id",
        //   element: <PartnerDetail />,
        // },
        {
          path: "/committee-members",
          element: <CommitteeMembers />,
        },
        {
          path: "/committee-members/:id",
          element: <CommiteeMemberDetail />,
        },
        {
          path: "/work-teams",
          element: <WorkTeams />,
        },
        {
          path: "/work-teams/:id",
          element: <WorkTeamDetail />,
        },
        {
          path: "/arbitration-uae",
          element: <ArbitrationUAE />,
        },
        {
          path: "/apply-arbitrator",
          element: <ApplyArbitrator />,
        },
        {
          path: "/mediation-request-form",
          element: <MediationRequestForm />,
        },
        {
          path: "/code-of-conduct",
          element: <CodeOfConduct />,
        },
        {
          path: "/arbitration-mediation-logistics",
          element: <ArbitrationMediationLogistics />,
        },
        {
          path: "/arbitration-mediation-logistics/halls/:id",
          element: <HallDetail />,
        },
        {
          path: "/center-tables",
          element: <CenterTable />,
        },
        {
          path: "membership",
          element: <Membership />,
        },
        {
          path: "departments-Of-Center",
          element: <DepartmentsOfCenter />,
        },
        {
          path: "faqs/:id",
          element: <Faqs />,
        },
        {
          path: "contact-us",
          element: <ContactUs />,
        },
        {
          path: "/role-of-center",
          element: <RoleOfTheCenter />,
        },
        {
          path: "/privacy-policy",
          element: <PrivacyPolicy />,
        },
        {
          path: "/terms-and-conditions",
          element: <TermsAndCondition />,
        },
        {
          path: "/refund-cancellation-policy",
          element: <RefundCancellationPolicy />,
        },
        {
          path: "/compliance-applicable-laws",
          element: <ComplianceApplicableLaws />,
        },
        {
          path: "/arbitration-clause",
          element: <ArbitrationClause />,
        },
        {
          path: "/intellectual-property-notice",
          element: <IntellectualPropertyNotice />,
        },
        {
          path: "/member-of-arbitrators",
          element: <MemberOfArbitrators />,
        },
        {
          path: "/arbitrator",
          element: <Arbitrator />,
        },

        // Courses drop down
        {
          path: "/conference",
          element: <Conference />,
        },
        {
          path: "/arbitration-training-program",
          element: <ArbitrationTrainingProgram />,
        },
        {
          path: "/seminar",
          element: <Seminar />,
        },
        {
          path: "/diploma-course-of-international-arbitration",
          element: <DiplomaCourseInternationalArbitration />,
        },
        {
          path: "/apply-expert",
          element: <ApplyExpert />,
        },
      ],
    },
    {
      path: "/account-verified",
      element: <AccountVerified />,
    },
    {
      path: "/auth/uaepass/callback",
      element: <UaeAccountVerified />,
    },
    {
      path: "/profile",
      element: (
        // <ProtectedRoute>
        <Profile />
        // </ProtectedRoute>
      ),
    },
    {
      element: <AdminLayout />,
      children: [
        {
          path: "/dashboard",
          children: [
            {
              path: "",
              element: (
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/arbitration",
              element: (
                // <ProtectedRoute>
                <Arbitration />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/copy",
              element: (
                // <ProtectedRoute>
                <CertifiedCopy />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/interpretation",
              element: (
                // <ProtectedRoute>
                <Interpretation />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/intervention",
              element: (
                // <ProtectedRoute>
                <Intervention />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/joinder",
              element: (
                // <ProtectedRoute>
                <Joinder />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/claim",
              element: (
                // <ProtectedRoute>
                <Claim />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/objection",
              element: (
                // <ProtectedRoute>
                <Objection />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/revocation",
              element: (
                // <ProtectedRoute>
                <Revocation />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/request/certificate",
              element: (
                // <ProtectedRoute>
                <Certificate />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/non-listed/arbitrator",
              element: (
                // <ProtectedRoute>
                <NonListedArbitrator />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/arbitrator/not-governed",
              element: (
                // <ProtectedRoute>
                <ArbitratorNotGoverned />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/membership",
              element: (
                // <ProtectedRoute>
                <Memberships />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/membership/arbitrator",
              element: (
                // <ProtectedRoute>
                <ArbitratorRegistration />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/membership/expert-registration",
              element: (
                // <ProtectedRoute>
                <ExpertRegistration />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services/membership/membership",
              element: (
                // <ProtectedRoute>
                <MembershipRegistration />
                // </ProtectedRoute>
              ),
            },
            {
              path: "e-services",
              element: (
                // <ProtectedRoute>
                <E_Services />
                // </ProtectedRoute>
              ),
            },
            {
              path: "arbitrator",
              element: (
                // <ProtectedRoute>
                <ArbitratorDashboard />
                // </ProtectedRoute>
              ),
            },
            {
              path: "arbitrator/profile",
              element: (
                // <ProtectedRoute>
                <ArbitratorProfile />
                // </ProtectedRoute>
              ),
            },
            {
              path: "arbitrator/application",
              element: (
                // <ProtectedRoute>
                <UserApplication />
                // </ProtectedRoute>
              ),
            },
            {
              path: "arbitrator/application/status",
              element: (
                // <ProtectedRoute>
                <ApplicationStatus />
                // </ProtectedRoute>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ]);

  return <RouterProvider router={router} />;
}

export default Routes;
